import React, { useEffect, useState } from 'react';
import { Shield, Lock, Target, CheckCircle, Mail, Phone, ArrowRight, FileText, Users, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        toast({
          title: "Thank you for your enquiry",
          description: "We'll be in touch within 24 hours.",
        });
        setFormData({ name: '', email: '', company: '', message: '' });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Submission failed",
        description: "Please try again or contact us directly.",
        variant: "destructive",
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950"></div>
        <div className="absolute inset-0 data-flow-bg opacity-20"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-fade-in-up">
            <p className="text-cautela-green text-sm font-medium tracking-wider uppercase mb-4">Specialist UK Cyber Assurance</p>
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Assuring Secure Digital<br />
              <span className="text-cautela-green">Capabilities for Government</span>
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-10 leading-relaxed">
              We embed Secure by Design principles across the technology lifecycle, providing resilient assurance for government and critical supply chains.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-cautela-purple hover:bg-cautela-purple-dark text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                onClick={() => document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Our Expertise
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-cautela-green text-cautela-green hover:bg-cautela-green hover:text-slate-950 px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:scale-105"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Contact a Consultant
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Key Pillars Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="pillar-card group border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-cautela-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Shield className="text-cautela-green" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Secure by Design</h3>
                <p className="text-slate-600 leading-relaxed">
                  Proactive security embedding from the earliest concept phase through to ongoing assurance.
                </p>
              </CardContent>
            </Card>

            <Card className="pillar-card group border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-cautela-purple/10 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Target className="text-cautela-purple" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Mission Critical</h3>
                <p className="text-slate-600 leading-relaxed">
                  Specialist expertise in UK MOD and Government standards for critical digital environments.
                </p>
              </CardContent>
            </Card>

            <Card className="pillar-card group border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-cautela-green/10 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Lock className="text-cautela-green" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Supply Chain</h3>
                <p className="text-slate-600 leading-relaxed">
                  Strengthening resilience across the government supply chain through disciplined risk management.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Capabilities Section */}
      <section id="capabilities" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Core Capabilities
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Cyber Security Assessment and Advisory Services
            </p>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              We bridge the gap between complex regulatory requirements and practical, resilient digital outcomes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FileText,
                title: 'Cyber Risk Assessment',
                description: 'Rigorous assessment of cyber risks aligned with UK MOD and Government standards for complex digital estates.'
              },
              {
                icon: Shield,
                title: 'Security Architecture',
                description: 'Designing resilient systems and services that embed security at the structural level using high-assurance patterns.'
              },
              {
                icon: CheckCircle,
                title: 'Assurance Frameworks',
                description: 'Guiding clients through compliance with mission-critical assurance requirements and regulatory standards.'
              },
              {
                icon: Target,
                title: 'Secure by Design Transformation',
                description: 'Operationalising SbD principles across the technology lifecycle to deliver lasting security outcomes.'
              },
              {
                icon: TrendingUp,
                title: 'Threat-Informed Analysis',
                description: 'Strategic insight into emerging threats to inform risk management and strengthen overall security posture.'
              },
              {
                icon: Users,
                title: 'Security Culture & Advisory',
                description: 'Enhancing organisational security culture through strategic partnership and disciplined assurance.'
              }
            ].map((capability, index) => (
              <Card key={index} className="capability-card group border-slate-200 hover:border-cautela-green transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-cautela-purple/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-cautela-green/10 transition-colors duration-300">
                    <capability.icon className="text-cautela-purple group-hover:text-cautela-green transition-colors duration-300" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{capability.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{capability.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Methodology
            </h2>
            <p className="text-xl text-slate-600">The Secure by Design Lifecycle</p>
          </div>

          <div className="mb-12">
            <p className="text-lg text-slate-700 max-w-4xl mx-auto text-center leading-relaxed">
              We partner with government organisations to ensure security is never an afterthought. Our proactive approach supports compliance, risk management, and operational confidence in mission-critical environments.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {[
              { label: 'Integrated Governance, Risk and Compliance' },
              { label: 'Demonstrable regulatory compliance' },
              { label: 'Enhanced risk management confidence' },
              { label: 'Strengthened security culture' }
            ].map((outcome, index) => (
              <div key={index} className="flex items-start gap-3 outcome-item">
                <CheckCircle className="text-cautela-green flex-shrink-0 mt-1" size={24} />
                <p className="text-slate-700 text-lg">{outcome.label}</p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {[
              {
                phase: '01',
                title: 'Concept & Design',
                subtitle: 'Secure Foundations',
                description: 'Embedding security requirements from the earliest concept phase, ensuring security is a core design driver.'
              },
              {
                phase: '02',
                title: 'Development',
                subtitle: 'Resilient Engineering',
                description: 'Applying Secure by Design principles throughout development to minimize vulnerabilities and enhance system resilience.'
              },
              {
                phase: '03',
                title: 'Deployment',
                subtitle: 'Hardened Delivery',
                description: 'Ensuring secure configuration and rigorous testing during deployment to meet mission-critical standards.'
              },
              {
                phase: '04',
                title: 'Ongoing Assurance',
                subtitle: 'Continuous Resilience',
                description: 'Continuous improvement and threat-informed assurance to maintain a robust security posture post-deployment.'
              }
            ].map((phase, index) => (
              <Card key={index} className="phase-card group border-none shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white">
                <CardContent className="p-8">
                  <div className="text-6xl font-bold text-cautela-green/20 mb-4 group-hover:text-cautela-green/40 transition-colors duration-300">
                    {phase.phase}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Phase {phase.phase}: {phase.title}</h3>
                  <p className="text-cautela-purple font-semibold mb-3">{phase.subtitle}</p>
                  <p className="text-slate-600 text-sm leading-relaxed">{phase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 data-flow-bg opacity-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Partner with Specialist UK
              <br />
              <span className="text-cautela-green">Cyber Assurance Consultants</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              Join us in shaping cyber-secure futures — where strategic insight meets disciplined assurance, and government-grade security meets real-world impact.
            </p>
          </div>

          <div className="max-w-2xl mx-auto">
            <Card className="border-none shadow-2xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-6">Contact Us</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="john.smith@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                      Organisation
                    </label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full"
                      placeholder="Your organisation"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      className="w-full"
                      placeholder="Tell us about your cyber assurance needs..."
                    />
                  </div>
                  <Button 
                    type="submit"
                    size="lg"
                    className="w-full bg-cautela-purple hover:bg-cautela-purple-dark text-white transition-all duration-300 hover:scale-[1.02]"
                  >
                    Submit Enquiry
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-16 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center justify-items-center">
            {[
              { label: 'HMG Standards' },
              { label: 'MOD Compliant' },
              { label: 'Supply Chain' },
              { label: 'NCSC Aligned' }
            ].map((badge, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-lg mb-3">
                  <Shield className="text-cautela-purple" size={32} />
                </div>
                <p className="text-sm font-semibold text-slate-700">{badge.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;